/**
 * Company Context Service
 * 
 * Manages company context data stored in Firestore at:
 * /customers/{customerId}/companyContext
 * 
 * Schema designed to support:
 * - Manual onboarding input
 * - HubSpot Enrichment API integration
 * - Future data enrichment sources
 * 
 * @module companyContextService
 */

import { db } from "@/lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Source of the data - tracks where each piece of information came from
 */
export type DataSource = 
  | 'manual'           // User entered during onboarding
  | 'hubspot'          // HubSpot Enrichment API
  | 'clearbit'         // Future: Clearbit enrichment
  | 'linkedin'         // Future: LinkedIn enrichment
  | 'import'           // Imported from external system
  | 'ai_inferred';     // AI-inferred from other data

/**
 * Metadata for tracking data provenance
 */
export interface DataProvenance {
  source: DataSource;
  sourceId?: string;           // External ID from source (e.g., HubSpot company ID)
  confidence?: number;         // 0-1 confidence score for enriched data
  lastUpdated: Timestamp | Date;
  lastUpdatedBy?: string;      // User ID if manual
}

/**
 * Basic company identification
 */
export interface CompanyIdentity {
  name?: string;
  legalName?: string;
  domain: string;
  alternativeDomains?: string[];
  abnAcn?: string;               // Australian Business Number / Company Number
  logo?: string;
  favicon?: string;
}

/**
 * Company size and structure metrics
 */
export interface CompanyMetrics {
  employeeCount?: number;
  employeeRange?: string;      // e.g., "50-100", "100-500"
  officeCount?: number;
  annualRevenue?: number;
  revenueRange?: string;       // e.g., "$1M-$10M"
  yearFounded?: number;
}

/**
 * Geographic information
 */
export interface CompanyGeography {
  headquarters?: {
    city?: string;
    state?: string;
    country?: string;
    countryCode?: string;      // ISO 3166-1 alpha-2
    timezone?: string;
    latitude?: number;
    longitude?: number;
  };
  operatingRegions?: string[]; // Countries/regions where company operates
  dataResidencyRegions?: string[]; // Where data is stored (for compliance)
  /** Indicates if the geography was estimated (true) or entered manually (false) */
  isEstimated?: boolean;
  /** Indicates if the user confirmed the estimated location was correct */
  isConfirmed?: boolean;
  /** How the geography was determined */
  estimationMethod?: 'geolocation' | 'ip_lookup' | 'user_input' | 'enrichment';
}

/**
 * Industry and classification
 */
export interface CompanyClassification {
  industry?: string;
  industryGroup?: string;
  sector?: string;
  subIndustry?: string;
  naicsCode?: string;
  sicCode?: string;
  tags?: string[];             // Custom tags for categorization
}

/**
 * Social and web presence
 */
export interface CompanySocial {
  linkedin?: string;
  twitter?: string;
  facebook?: string;
  crunchbase?: string;
  github?: string;
  website?: string;
}

/**
 * Technology stack (useful for compliance/security context)
 */
export interface CompanyTechStack {
  technologies?: string[];     // Detected or declared technologies
  cloudProviders?: string[];   // AWS, GCP, Azure, etc.
  securityTools?: string[];    // Security/compliance tools in use
}

/**
 * HubSpot-specific enrichment data
 * Structured to match HubSpot Company API response
 */
export interface HubSpotEnrichment {
  companyId?: string;
  enrichedAt?: Timestamp | Date;
  properties?: {
    name?: string;
    domain?: string;
    industry?: string;
    type?: string;
    description?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    numberofemployees?: number;
    annualrevenue?: number;
    founded_year?: string;
    linkedin_company_page?: string;
    twitterhandle?: string;
    facebook_company_page?: string;
    timezone?: string;
    total_revenue?: number;
    web_technologies?: string;
    // Raw properties for any additional HubSpot fields
    [key: string]: any;
  };
  rawResponse?: any;           // Store full response for debugging
}


/**
 * Result from location estimation
 */
export interface LocationEstimate {
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  isEstimated: boolean;
  /** Indicates if the user confirmed the estimated location was correct */
  isConfirmed?: boolean;
  estimationMethod: 'geolocation' | 'ip_lookup' | 'user_input' | 'enrichment';
}


/**
 * Main Company Context document structure
 */
export interface CompanyContext {
  // Core identification
  identity: CompanyIdentity;
  
  // Size and metrics
  metrics: CompanyMetrics;
  
  // Location info
  geography: CompanyGeography;
  
  // Industry classification
  classification: CompanyClassification;
  
  // Social presence
  social: CompanySocial;
  
  // Tech stack
  techStack: CompanyTechStack;
  
  // Enrichment data from external sources
  enrichment?: {
    hubspot?: HubSpotEnrichment;
    // Future enrichment sources
    clearbit?: any;
    linkedin?: any;
  };
  
  // Data provenance tracking
  provenance: {
    identity?: Partial<Record<keyof CompanyIdentity, DataProvenance>>;
    metrics?: Partial<Record<keyof CompanyMetrics, DataProvenance>>;
    geography?: DataProvenance;
    classification?: DataProvenance;
    social?: DataProvenance;
    techStack?: DataProvenance;
  };
  
  // Metadata
  metadata: {
    createdAt: Timestamp | Date;
    createdBy?: string;
    updatedAt: Timestamp | Date;
    updatedBy?: string;
    version: number;           // Schema version for migrations
    onboardingComplete?: boolean;
  };
}

// ============================================================================
// Service Functions
// ============================================================================

/**
 * Recursively remove undefined values from an object
 * Firestore doesn't accept undefined values in updateDoc()
 */
function stripUndefined<T extends Record<string, any>>(obj: T): T {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      const stripped = stripUndefined(value);
      // Only include if the object has properties after stripping
      if (Object.keys(stripped).length > 0) {
        result[key] = stripped;
      }
    } else {
      result[key] = value;
    }
  }
  return result as T;
}

/**
 * Get the company context document reference
 */
function getCompanyContextRef(customerId: string) {
  return doc(db, "customers", customerId, "companyContext", "main");
}

/**
 * Get the current company context
 */
export async function getCompanyContext(customerId: string): Promise<CompanyContext | null> {
  try {
    const docRef = getCompanyContextRef(customerId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return docSnap.data() as CompanyContext;
  } catch (error) {
    console.error("[CompanyContextService] Error getting company context:", error);
    throw error;
  }
}

/**
 * Helper to create provenance entry (only includes lastUpdatedBy if userId is provided)
 */
function createProvenance(userId?: string): DataProvenance {
  const provenance: DataProvenance = {
    source: 'manual',
    lastUpdated: new Date(),
  };
  if (userId) {
    provenance.lastUpdatedBy = userId;
  }
  return provenance;
}

/**
 * Try to get location from browser geolocation API
 * This will trigger the browser permission prompt and wait for user response
 */
export async function tryBrowserGeolocation(): Promise<{ latitude: number; longitude: number } | null> {
  if (typeof window === 'undefined' || !navigator.geolocation) {
    return null;
  }
  
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        // User denied or error - resolve with null
        console.log("[CompanyContextService] Geolocation error:", error.message);
        resolve(null);
      },
      { 
        enableHighAccuracy: false, // Don't need high accuracy for city-level
        timeout: 30000, // 30s timeout to give user time to respond to permission prompt
        maximumAge: 300000 // Cache for 5 min
      }
    );
  });
}

/**
 * Reverse geocode coordinates to get location details
 */
async function reverseGeocode(lat: number, lon: number): Promise<Partial<LocationEstimate> | null> {
  try {
    // Using a free reverse geocoding API (BigDataCloud)
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
    );
    if (!response.ok) return null;
    
    const data = await response.json();
    return {
      city: data.city || data.locality,
      state: data.principalSubdivision,
      country: data.countryName,
      countryCode: data.countryCode,
      latitude: lat,
      longitude: lon,
    };
  } catch {
    return null;
  }
}

/**
 * Get location estimate from IP address
 */
export async function getLocationFromIP(): Promise<Partial<LocationEstimate> | null> {
  try {
    // Using ipapi.co free tier (1000 requests/day)
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) return null;
    
    const data = await response.json();
    return {
      city: data.city,
      state: data.region,
      country: data.country_name,
      countryCode: data.country_code,
      timezone: data.timezone,
      latitude: data.latitude,
      longitude: data.longitude,
    };
  } catch {
    return null;
  }
}

/**
 * Estimate user's geographic location
 * Tries browser geolocation first, falls back to IP-based lookup
 */
export async function estimateLocation(): Promise<LocationEstimate | null> {
  // Try browser geolocation first (more accurate if user allows)
  const browserCoords = await tryBrowserGeolocation();
  
  if (browserCoords) {
    const geoDetails = await reverseGeocode(browserCoords.latitude, browserCoords.longitude);
    if (geoDetails) {
      return {
        ...geoDetails,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isEstimated: true,
        estimationMethod: 'geolocation',
      } as LocationEstimate;
    }
  }
  
  // Fall back to IP-based lookup
  const ipLocation = await getLocationFromIP();
  if (ipLocation) {
    return {
      ...ipLocation,
      isEstimated: true,
      estimationMethod: 'ip_lookup',
    } as LocationEstimate;
  }
  
  // Last resort: just get timezone
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    isEstimated: true,
    estimationMethod: 'ip_lookup',
  };
}

/**
 * Create initial company context from onboarding
 */
export async function createCompanyContext(
  customerId: string,
  data: {
    domain: string;
    companyName?: string;
    abnAcn?: string;
    employeeCount?: number;
    officeCount?: number;
    /** Pre-determined location (from user choice in modal) */
    location?: LocationEstimate | null;
  },
  userId?: string
): Promise<void> {
  try {
    const docRef = getCompanyContextRef(customerId);
    const now = serverTimestamp();
    
    // Build identity provenance (only include fields that have values)
    const identityProvenance: Partial<Record<keyof CompanyIdentity, DataProvenance>> = {
      domain: createProvenance(userId),
    };
    if (data.companyName) {
      identityProvenance.name = createProvenance(userId);
    }
    if (data.abnAcn) {
      identityProvenance.abnAcn = createProvenance(userId);
    }
    
    // Build metrics provenance (only include fields that have values)
    const metricsProvenance: Partial<Record<keyof CompanyMetrics, DataProvenance>> = {};
    if (data.employeeCount) {
      metricsProvenance.employeeCount = createProvenance(userId);
    }
    if (data.officeCount) {
      metricsProvenance.officeCount = createProvenance(userId);
    }
    
    // Build metadata (only include userId fields if provided)
    const metadata: CompanyContext['metadata'] = {
      createdAt: now as any,
      updatedAt: now as any,
      version: 1,
      onboardingComplete: false,
    };
    if (userId) {
      metadata.createdBy = userId;
      metadata.updatedBy = userId;
    }
    
    // Build identity (only include name and abnAcn if provided)
    const identity: CompanyIdentity = {
      domain: data.domain,
    };
    if (data.companyName) {
      identity.name = data.companyName;
    }
    if (data.abnAcn) {
      identity.abnAcn = data.abnAcn;
    }
    
    // Build metrics (only include fields with values)
    const metrics: CompanyMetrics = {};
    if (data.employeeCount) {
      metrics.employeeCount = data.employeeCount;
    }
    if (data.officeCount) {
      metrics.officeCount = data.officeCount;
    }
    
    // Build geography from passed location data
    let geography: CompanyGeography = {};
    if (data.location) {
      geography = {
        headquarters: {
          city: data.location.city,
          state: data.location.state,
          country: data.location.country,
          countryCode: data.location.countryCode,
          timezone: data.location.timezone,
          latitude: data.location.latitude,
          longitude: data.location.longitude,
        },
        isEstimated: data.location.isEstimated,
        isConfirmed: data.location.isConfirmed,
        estimationMethod: data.location.estimationMethod,
      };
      // Remove undefined values from headquarters
      if (geography.headquarters) {
        geography.headquarters = Object.fromEntries(
          Object.entries(geography.headquarters).filter(([_, v]) => v !== undefined)
        ) as CompanyGeography['headquarters'];
      }
      console.log(`[CompanyContextService] Location via ${data.location.estimationMethod}:`, data.location.city, data.location.country, data.location.isConfirmed ? '(confirmed)' : '(unconfirmed)');
    }
    
    // Build provenance object
    const provenance: CompanyContext['provenance'] = {
      identity: identityProvenance,
      metrics: metricsProvenance,
    };
    
    // Add geography provenance if we have an estimate
    if (geography.isEstimated) {
      provenance.geography = {
        source: geography.estimationMethod === 'geolocation' ? 'manual' : 'ai_inferred',
        lastUpdated: new Date(),
        confidence: geography.estimationMethod === 'geolocation' ? 0.9 : 0.7,
      };
    }
    
    const context: CompanyContext = {
      identity,
      metrics,
      geography,
      classification: {},
      social: {
        website: data.domain.startsWith('http') ? data.domain : `https://${data.domain}`,
      },
      techStack: {},
      provenance,
      metadata,
    };
    
    await setDoc(docRef, context);
    console.log("[CompanyContextService] Created company context for:", customerId);
  } catch (error) {
    console.error("[CompanyContextService] Error creating company context:", error);
    throw error;
  }
}

/**
 * Update company context with partial data
 */
export async function updateCompanyContext(
  customerId: string,
  updates: Partial<CompanyContext>,
  userId?: string
): Promise<void> {
  try {
    const docRef = getCompanyContextRef(customerId);
    
    await updateDoc(docRef, {
      ...updates,
      'metadata.updatedAt': serverTimestamp(),
      'metadata.updatedBy': userId,
    });
    
    console.log("[CompanyContextService] Updated company context for:", customerId);
  } catch (error) {
    console.error("[CompanyContextService] Error updating company context:", error);
    throw error;
  }
}

/**
 * Update company context with HubSpot enrichment data
 */
export async function enrichWithHubSpot(
  customerId: string,
  hubspotData: HubSpotEnrichment
): Promise<void> {
  try {
    const docRef = getCompanyContextRef(customerId);
    const existing = await getCompanyContext(customerId);
    
    if (!existing) {
      throw new Error("Company context not found. Create it first.");
    }
    
    // Map HubSpot properties to our schema
    const updates: Partial<CompanyContext> = {
      enrichment: {
        ...existing.enrichment,
        hubspot: {
          ...hubspotData,
          enrichedAt: new Date(),
        },
      },
    };
    
    // Auto-fill empty fields from HubSpot data
    const props = hubspotData.properties || {};
    
    if (!existing.identity.name && props.name) {
      updates.identity = { ...existing.identity, name: props.name };
    }
    
    if (!existing.metrics.employeeCount && props.numberofemployees) {
      updates.metrics = { ...existing.metrics, employeeCount: props.numberofemployees };
    }
    
    if (!existing.metrics.annualRevenue && props.annualrevenue) {
      updates.metrics = { ...updates.metrics || existing.metrics, annualRevenue: props.annualrevenue };
    }
    
    if (!existing.metrics.yearFounded && props.founded_year) {
      updates.metrics = { ...updates.metrics || existing.metrics, yearFounded: parseInt(props.founded_year) };
    }
    
    if (!existing.geography.headquarters?.city && props.city) {
      updates.geography = {
        ...existing.geography,
        headquarters: {
          ...existing.geography.headquarters,
          city: props.city,
          state: props.state,
          country: props.country,
          timezone: props.timezone,
        },
      };
    }
    
    if (!existing.classification.industry && props.industry) {
      updates.classification = { ...existing.classification, industry: props.industry };
    }
    
    // Only update social fields that have actual values
    const socialUpdates: Partial<CompanySocial> = {};
    if (props.linkedin_company_page) socialUpdates.linkedin = props.linkedin_company_page;
    else if (existing.social.linkedin) socialUpdates.linkedin = existing.social.linkedin;
    if (props.twitterhandle) socialUpdates.twitter = props.twitterhandle;
    else if (existing.social.twitter) socialUpdates.twitter = existing.social.twitter;
    if (props.facebook_company_page) socialUpdates.facebook = props.facebook_company_page;
    else if (existing.social.facebook) socialUpdates.facebook = existing.social.facebook;
    
    if (Object.keys(socialUpdates).length > 0) {
      updates.social = {
        ...existing.social,
        ...socialUpdates,
      };
    }
    
    if (props.web_technologies) {
      const techs = props.web_technologies.split(';').map((t: string) => t.trim()).filter(Boolean);
      updates.techStack = { ...existing.techStack, technologies: techs };
    }
    
    // Strip undefined values before updating Firestore
    const cleanUpdates = stripUndefined(updates);
    
    await updateDoc(docRef, {
      ...cleanUpdates,
      'metadata.updatedAt': serverTimestamp(),
    });
    
    console.log("[CompanyContextService] Enriched with HubSpot data for:", customerId);
  } catch (error) {
    console.error("[CompanyContextService] Error enriching with HubSpot:", error);
    throw error;
  }
}

/**
 * Mark onboarding as complete
 */
export async function markOnboardingComplete(customerId: string): Promise<void> {
  try {
    const docRef = getCompanyContextRef(customerId);
    await updateDoc(docRef, {
      'metadata.onboardingComplete': true,
      'metadata.updatedAt': serverTimestamp(),
    });
  } catch (error) {
    console.error("[CompanyContextService] Error marking onboarding complete:", error);
    throw error;
  }
}

/**
 * Get a simplified context object for AI/onboarding use
 * (Backwards compatible with existing localStorage format)
 */
export async function getSimplifiedContext(customerId: string): Promise<{
  domain: string;
  companyName?: string;
  teamSize?: number;
  officesCount?: number;
} | null> {
  const context = await getCompanyContext(customerId);
  if (!context) return null;
  
  return {
    domain: context.identity.domain,
    companyName: context.identity.name,
    teamSize: context.metrics.employeeCount,
    officesCount: context.metrics.officeCount,
  };
}

// ============================================================================
// Auto-Enrichment Functions
// ============================================================================

/**
 * Result from HubSpot enrichment API call
 */
export interface HubSpotEnrichmentResult {
  success: boolean;
  data?: HubSpotEnrichment;
  error?: string;
  notFound?: boolean;
}

/**
 * Fetch company enrichment data from HubSpot via our backend API
 * @param domain - The company domain to search for
 * @returns Enrichment data or error
 */
export async function fetchHubSpotEnrichment(domain: string): Promise<HubSpotEnrichmentResult> {
  try {
    console.log("[CompanyContextService] Fetching HubSpot enrichment for domain:", domain);
    
    // Use deiterateAPI utility for consistent API calls with auth    
    const result = await deiterateAPI.POST<{ success: boolean; data: HubSpotEnrichment }>('/hubspot-enrich/company', {
      body: JSON.stringify({ domain }),
    });
    
    console.log("[CompanyContextService] HubSpot enrichment received:", result.data?.properties?.name);
    
    return { success: true, data: result.data };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    
    // Check if it's a 404 (company not found)
    if (errorMessage.includes('404')) {
      console.log("[CompanyContextService] No HubSpot company found for domain:", domain);
      return { success: false, notFound: true, error: "Company not found in HubSpot" };
    }
    
    console.error("[CompanyContextService] Error fetching HubSpot enrichment:", error);
    return { 
      success: false, 
      error: errorMessage
    };
  }
}

/**
 * Auto-enrich company context with HubSpot data
 * This fetches data from HubSpot and applies it to the company context
 * 
 * @param customerId - The customer ID
 * @param domain - The company domain to enrich
 * @returns The enrichment result with the data that was applied
 */
export async function autoEnrichWithHubSpot(
  customerId: string,
  domain: string
): Promise<HubSpotEnrichmentResult> {
  try {
    // Fetch enrichment data from HubSpot
    const enrichmentResult = await fetchHubSpotEnrichment(domain);
    
    if (!enrichmentResult.success || !enrichmentResult.data) {
      return enrichmentResult;
    }
    
    // Apply the enrichment to the company context
    await enrichWithHubSpot(customerId, enrichmentResult.data);
    
    console.log("[CompanyContextService] Auto-enrichment complete for:", customerId);
    return enrichmentResult;
  } catch (error) {
    console.error("[CompanyContextService] Error in auto-enrichment:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

/**
 * Create company context and automatically enrich with HubSpot data
 * This is a convenience function that combines creation and enrichment
 * 
 * @param customerId - The customer ID
 * @param data - Initial company data from onboarding
 * @param userId - Optional user ID for provenance tracking
 * @returns Object with creation success and enrichment result
 */
export async function createAndEnrichCompanyContext(
  customerId: string,
  data: {
    domain: string;
    companyName?: string;
    abnAcn?: string;
    employeeCount?: number;
    officeCount?: number;
    location?: LocationEstimate | null;
  },
  userId?: string
): Promise<{ created: boolean; enrichment: HubSpotEnrichmentResult }> {
  // First create the company context
  await createCompanyContext(customerId, data, userId);
  
  // Then attempt to enrich with HubSpot data
  const enrichmentResult = await autoEnrichWithHubSpot(customerId, data.domain);
  
  return {
    created: true,
    enrichment: enrichmentResult
  };
}
